import { ExitIcon } from '@radix-ui/react-icons';
import { Button, Flex, Spinner, Text } from '@radix-ui/themes';
import { useCallback, useEffect, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';

import { logout, selectIsAuthenticated } from '@/entities/session';
import {
  useLazyStaffStoreAppDetailsQuery,
  updateGlobalAPIBaseURLs,
  refreshAccessToken,
  useLazyStaffMeQuery,
} from '@/shared/api';
import {
  useAppDispatch,
  useAppSelector,
  useRedirectPath,
} from '@/shared/hooks';
import { appSession } from '@/shared/lib';
import { convertServicesToEnvironment } from '@/shared/lib/environment';
import { getAppKey } from '@/shared/lib/helpers';

export function ProtectedView() {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const [getAppDetails] = useLazyStaffStoreAppDetailsQuery();
  const [error, setError] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { storeKey = '' } = useParams();
  const { navigateToStoreSelection, navigateToLogin } = useRedirectPath();
  const [getStaffMe] = useLazyStaffMeQuery();

  const initializeEnvironment = useCallback(
    async (storeKey: string) => {
      try {
        const appDetails = await getAppDetails({
          store_key: storeKey,
          app_key: getAppKey(),
        }).unwrap();

        if (appDetails) {
          await refreshAccessToken(storeKey);

          const services = convertServicesToEnvironment(
            appDetails.result.services
          );
          updateGlobalAPIBaseURLs(services);
          setIsLoading(false);
        }
      } catch (error) {
        setError(error);
        setIsLoading(false);
      }
    },
    [getAppDetails]
  );

  useEffect(() => {
    if (!isAuthenticated) {
      navigateToLogin();
      setIsLoading(false);
    } else {
      if (storeKey) {
        Promise.all([
          initializeEnvironment(storeKey),
          getStaffMe(undefined, true).unwrap(),
        ]).catch((error) => {
          setError(error);
          setIsLoading(false);
        });
      } else {
        navigateToStoreSelection();
        setIsLoading(false);
      }
    }
  }, [
    isAuthenticated,
    storeKey,
    initializeEnvironment,
    navigateToLogin,
    navigateToStoreSelection,
    setError,
    setIsLoading,
    getStaffMe,
  ]);

  return (
    <>
      {isLoading ? (
        <Flex className="h-dvh" gap="2" justify="center" align="center">
          <Spinner size="3" />
          <Text color="gray" size="2">
            Starting app...
          </Text>
        </Flex>
      ) : error ? (
        <ErrorView error={error} />
      ) : (
        <Outlet />
      )}
    </>
  );
}

function ErrorView({ error }: { error: any }) {
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const { navigateToLoginAfterLogout } = useRedirectPath();
  return (
    <Flex className="h-dvh" gap="1" justify="center" align="center">
      {error.status && (
        <Text color="gray" size="2">
          {error.status}
        </Text>
      )}
      <Text color="gray" size="2">
        {error.data.detail || 'Unexpected error. Please try again.'}
      </Text>

      <Button
        loading={loading}
        className="absolute top-6 right-8"
        variant="ghost"
        color="gray"
        onClick={() => {
          setLoading(true);
          dispatch(logout())
            .unwrap()
            .then(() => {
              setLoading(false);
              appSession.removeItem();
              navigateToLoginAfterLogout();
            });
        }}
      >
        <ExitIcon /> Logout
      </Button>
    </Flex>
  );
}
